import React, { useEffect, useState } from 'react'
import { FaFile, FaFilePdf } from 'react-icons/fa'
import { useLocation } from 'react-router-dom'

import {
  Box,
  Center,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text
} from '@chakra-ui/react'

import * as api from '../../../../../api/midiaDigital'

function useDebounce<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState<T>(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// Mapeia extensão de arquivo para MIME type
function getMimeType(ext: string): string {
  const mimeMap: Record<string, string> = {
    '.mp4': 'video/mp4',
    '.webm': 'video/webm',
    '.ogg': 'video/ogg',
    '.mov': 'video/quicktime',
    '.avi': 'video/x-msvideo',
    '.mkv': 'video/x-matroska',
    '.flv': 'video/x-flv',
    '.3gp': 'video/3gpp',
    '.ts': 'video/mp2t',
    '.m3u8': 'application/x-mpegURL',
    '.m4v': 'video/x-m4v',
    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.aac': 'audio/aac',
    '.flac': 'audio/flac'
  }
  return mimeMap[ext.toLowerCase()] || 'video/mp4'
}

function getFileType(item: Imagem, ext: string): string {
  let fileType = 'unknown'

  // Verifica pelo tipoMidia da API primeiro (mais confiável)
  if (item.tipoMidia && item.tipoMidia.dscMidia) {
    const tipo = (item.tipoMidia.dscMidia || '').toLowerCase()
    if (tipo.includes('imagem') || tipo.includes('image')) return 'image'
    if (tipo.includes('video')) return 'video'
    if (tipo.includes('áudio') || tipo.includes('audio')) return 'audio'
    if (tipo.includes('pdf')) return 'pdf'
  }

  // Fallback pela extensão se tipoMidia não foi reconhecido
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']
  const videoExts = [
    '.mp4',
    '.webm',
    '.ogg',
    '.mov',
    '.avi',
    '.mkv',
    '.flv',
    '.3gp',
    '.ts',
    '.m3u8',
    '.m4v'
  ]
  const audioExts = ['.mp3', '.wav', '.aac', '.flac']
  const pdfExts = ['.pdf']

  if (imageExts.includes(ext)) fileType = 'image'
  else if (videoExts.includes(ext)) fileType = 'video'
  else if (audioExts.includes(ext)) fileType = 'audio'
  else if (pdfExts.includes(ext)) fileType = 'pdf'

  return fileType
}

interface Imagem {
  seqMidia: number
  dscMidia: string
  dscUriCdn: string
  dscDescricao?: string
  tipoMidia?: {
    codMidia: number
    dscMidia: string
  }
  datRegistro?: string
  dscDireitoAutoral?: string
}

interface GridItemProps {
  item: Imagem
  idx: number
  url: string
  ext: string
  fileType: string
  onSelectImage: (img: Imagem) => void
  onClose: () => void
}

const GridItem = ({
  item,
  idx,
  url,
  ext,
  fileType,
  onSelectImage,
  onClose
}: GridItemProps) => {
  return (
    <Box
      key={`${item.seqMidia}-${idx}`}
      textAlign="center"
      cursor="pointer"
      _hover={{ transform: 'scale(1.05)' }}
      transition="transform 0.2s"
      onClick={() => {
        onSelectImage(item)
        onClose()
      }}
    >
      <Box
        w="150px"
        h="100px"
        bg="#f0f0f0"
        borderRadius="4px"
        overflow="hidden"
        mx="auto"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {fileType === 'image' && (
          <Image
            src={url}
            alt={item.dscDescricao || item.dscMidia}
            w="100%"
            h="100%"
            objectFit="contain"
            display="block"
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        )}
        {fileType === 'video' && (
          <video
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          >
            <source src={url} type={getMimeType(ext)} />
            Seu navegador não suporta vídeo HTML5.
          </video>
        )}
        {fileType === 'audio' && (
          <audio
            controls
            style={{
              width: '100%',
              height: '100%'
            }}
          >
            <source src={url} type={getMimeType(ext)} />
            Seu navegador não suporta áudio HTML5.
          </audio>
        )}
        {fileType === 'pdf' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            w="100%"
            h="100%"
            fontSize="40px"
            color="#d32f2f"
            gap="10px"
          >
            <FaFilePdf />
          </Box>
        )}
        {fileType === 'unknown' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            w="100%"
            h="100%"
            fontSize="40px"
            color="#999"
            gap="10px"
          >
            <FaFile />
          </Box>
        )}
      </Box>

      <Text
        mt={2}
        fontSize="sm"
        color="gray.700"
        maxW="150px"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        mx="auto"
      >
        {item.dscMidia}
      </Text>
    </Box>
  )
}

interface BibliotecaModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectImage: (imagem: Imagem) => void
}

const BibliotecaModal: React.FC<BibliotecaModalProps> = ({
  isOpen,
  onClose,
  onSelectImage
}) => {
  const [midias, setMidias] = useState<Imagem[]>([])
  const [loading, setLoading] = useState(false)
  const [termoBusca, setTermoBusca] = useState('')
  const location = useLocation()
  const debounced = useDebounce(termoBusca, 500)

  useEffect(() => {
    if (!isOpen) return
    const q = new URLSearchParams(location.search).get('dscMidia') || ''
    setTermoBusca(q)
  }, [isOpen, location])

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setLoading(true)
      try {
        const { dados = [] } = await api.listarMidias(1, 1000, debounced)
        const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']
        const videoExts = [
          '.mp4',
          '.webm',
          '.ogg',
          '.mov',
          '.avi',
          '.mkv',
          '.flv',
          '.3gp',
          '.ts',
          '.m3u8',
          '.m4v'
        ]
        const audioExts = ['.mp3', '.wav', '.aac', '.flac']
        const pdfExts = ['.pdf']
        const allExts = [...imageExts, ...videoExts, ...audioExts, ...pdfExts]

        const filtro = dados.filter((item: any) => {
          // Primeiro, verifica pelo campo tipoMidia da API (mais confiável)
          if (item.tipoMidia && item.tipoMidia.dscMidia) {
            const tipo = (item.tipoMidia.dscMidia || '').toLowerCase()
            if (tipo.includes('imagem') || tipo.includes('image')) return true
            if (tipo.includes('video')) return true
            if (tipo.includes('áudio') || tipo.includes('audio')) return true
            if (tipo.includes('pdf')) return true
            // Se tipoMidia existe mas não é reconhecido, faz fallback para extensão
          }

          // Fallback: verifica pela extensão da URL
          const url = (item.dscUriCdn || item.dscMidia || '')
            .toLowerCase()
            .split('?')[0]
          const hasValidExt = allExts.some((ext) => url.endsWith(ext))

          if (hasValidExt) return true

          // Extra: se não tem extensão reconhecida mas tem um dscMidia com nome de arquivo válido
          if (item.dscMidia) {
            const nomeMidia = (item.dscMidia || '').toLowerCase()
            return allExts.some((ext) => nomeMidia.endsWith(ext))
          }

          return false
        })

        console.debug(
          `[BibliotecaModal] API retornou ${dados.length} mídias, ${filtro.length} passaram no filtro`,
          {
            totalAPI: dados.length,
            filterado: filtro.length,
            termoBusca: debounced
          }
        )

        setMidias(filtro)
      } catch (e) {
        console.error('[BibliotecaModal] Erro ao listar mídias:', e)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [isOpen, debounced])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent mx="4" maxH="90vh" overflow="hidden" p="6">
        <ModalHeader>Selecione uma mídia</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <Input
            placeholder="Buscar por nome da mídia"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            mb={4}
          />

          {loading ? (
            <Center h="200px">
              <Spinner size="xl" color="blue.500" />
            </Center>
          ) : midias.length === 0 ? (
            <Center>
              <Text fontSize="lg" color="gray.500">
                Não há mídias encontradas.
              </Text>
            </Center>
          ) : (
            <SimpleGrid columns={[3, 4, 5]} spacing={4}>
              {midias.map((item, idx) => {
                const url = decodeURIComponent(item.dscUriCdn || item.dscMidia)
                const ext = url
                  .split('?')[0]
                  .slice(url.lastIndexOf('.'))
                  .toLowerCase()
                const fileType = getFileType(item, ext)

                return (
                  <GridItem
                    key={`${item.seqMidia}-${idx}`}
                    item={item}
                    idx={idx}
                    url={url}
                    ext={ext}
                    fileType={fileType}
                    onSelectImage={onSelectImage}
                    onClose={onClose}
                  />
                )
              })}
            </SimpleGrid>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  ) as any
}

export default BibliotecaModal
